import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Target, User, Video } from 'lucide-react';
import { Client } from '../../types/client';
import { clientService } from '../../services/clientService';
import { detectPlatform, shouldOfferBot } from '../../services/conferenceUrlDetection';
import { resolveLocalInstant, type LocalInstant } from '../../utils/localInstant';

interface CreateMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateMeeting: (meetingData: MeetingCreationData) => void;
  isLoading?: boolean;
  /** TASK-644: preselect this client once the client list loads (still changeable). */
  preselectedClientId?: string;
}

export interface MeetingCreationData {
  client_id: string;
  client_name: string;
  scheduled_at: string;
  meeting_goal?: string;
  duration_minutes: number;
  meeting_type: string;
  // TASK-425: optional Teams/Meet URL + opt-in Attendee.dev auto-join.
  // Only forwarded to the backend when a recognised platform URL is present
  // and the toggle is on; the backend re-validates with detectPlatform.
  meeting_url?: string;
  auto_join_with_bot?: boolean;
}

interface ClientSelectorProps {
  selectedClient: Client | null;
  onClientSelect: (client: Client) => void;
  clients: Client[];
  isLoading: boolean;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  selectedClient,
  onClientSelect,
  clients,
  isLoading
}) => {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <User className="w-4 h-4 inline mr-2" />
        Select Client
      </label>
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading clients...</div>
      ) : (
        <select
          value={selectedClient?.id || ''}
          onChange={(e) => {
            const client = clients.find(c => c.id === e.target.value);
            if (client) onClientSelect(client);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        >
          <option value="">Select a client...</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name} ({client.email})
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({
  isOpen,
  onClose,
  onCreateMeeting,
  isLoading = false,
  preselectedClientId
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [meetingGoal, setMeetingGoal] = useState('');
  const [duration, setDuration] = useState(60);
  const [meetingType, setMeetingType] = useState('advisory');
  // TASK-425: Teams/Meet URL + auto-join toggle.
  const [meetingUrl, setMeetingUrl] = useState('');
  const [autoJoinWithBot, setAutoJoinWithBot] = useState(true);
  // TASK-674 review: set only when the typed wall clock is a DST gap or overlap, so the
  // RM picks the instant instead of the runtime guessing one. Cleared on submit and reset.
  const [dstChoice, setDstChoice] = useState<LocalInstant | null>(null);

  const detectedPlatform = detectPlatform(meetingUrl);
  const showAutoJoinToggle = shouldOfferBot(meetingUrl);

  // Fetch clients when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduledDate(tomorrow.toISOString().split('T')[0]);
      setScheduledTime('09:00');
    }
  }, [isOpen]);

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const response = await clientService.fetchClients();
      const fetched = response.clients || [];
      setClients(fetched);
      if (preselectedClientId) {
        const match = fetched.find((c: Client) => c.id === preselectedClientId);
        if (match) setSelectedClient(match);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  /**
   * TASK-674: the RM enters wall-clock time in their business timezone (the browser's tz in
   * this app). It is parsed AS local time and converted to a real UTC instant — the old code
   * stamped a literal `Z`, declaring 09:00 Zurich to be 09:00 UTC and shifting the
   * appointment by the offset (1h winter / 2h summer).
   *
   * TASK-674 review: twice a year that mapping is not a function. `resolveLocalInstant`
   * reports the spring gap (the typed time does not exist) and the autumn overlap (it happens
   * twice) instead of letting JS pick — the AC's "explicit valid choice, never a silent
   * shift". `dstChoice` holds the RM's answer for exactly one submission.
   *
   * ponytail: browser tz = RM tz; a per-user tz picker is only needed if RMs schedule across
   * zones for each other — add then, not now. `account_settings.default_timezone` deliberately
   * does NOT feed this: it is the account's reporting default, and using it here would tell a
   * travelling RM their 09:00 means 09:00 somewhere else.
   */
  const submitWith = (scheduledDateTime: string) => {
    if (!selectedClient) return;
    const trimmedUrl = meetingUrl.trim();
    const meetingData: MeetingCreationData = {
      client_id: selectedClient.id,
      client_name: selectedClient.name,
      scheduled_at: scheduledDateTime,
      meeting_goal: meetingGoal.trim() || undefined,
      duration_minutes: duration,
      meeting_type: meetingType,
      meeting_url: trimmedUrl || undefined,
      auto_join_with_bot: shouldOfferBot(trimmedUrl) ? autoJoinWithBot : false,
    };

    setDstChoice(null);
    onCreateMeeting(meetingData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClient || !scheduledDate || !scheduledTime) {
      return;
    }

    const resolved = resolveLocalInstant(scheduledDate, scheduledTime);
    if (resolved.kind === 'invalid') return;
    if (resolved.kind === 'ok') {
      submitWith(resolved.iso);
      return;
    }
    // Gap or overlap: do NOT guess. Present the real instants and wait for the RM.
    setDstChoice(resolved);
  };

  const resetForm = () => {
    setSelectedClient(null);
    setScheduledDate('');
    setScheduledTime('');
    setMeetingGoal('');
    setDuration(60);
    setMeetingType('advisory');
    setMeetingUrl('');
    setAutoJoinWithBot(true);
    setDstChoice(null);
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Create New Meeting</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Client Selection */}
          <ClientSelector
            selectedClient={selectedClient}
            onClientSelect={setSelectedClient}
            clients={clients}
            isLoading={clientsLoading}
          />

          {/* Date */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Calendar className="w-4 h-4 inline mr-2" />
              Meeting Date
            </label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Time */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Clock className="w-4 h-4 inline mr-2" />
              Meeting Time
            </label>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Clock className="w-4 h-4 inline mr-2" />
              Duration (minutes)
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>

          {/* Meeting Goal */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Target className="w-4 h-4 inline mr-2" />
              Meeting Goal (optional)
            </label>
            <textarea
              value={meetingGoal}
              onChange={(e) => setMeetingGoal(e.target.value)}
              placeholder="e.g., Portfolio review, Investment strategy discussion, Risk assessment..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={3}
              maxLength={500}
            />
            <div className="text-xs text-gray-500">
              {meetingGoal.length}/500 characters
            </div>
          </div>

          {/* TASK-425: Teams/Meet URL + auto-join with bot */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <Video className="w-4 h-4 inline mr-2" />
              Meeting URL (optional)
            </label>
            <input
              type="url"
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              placeholder="https://teams.microsoft.com/l/meetup-join/... or https://meet.google.com/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {showAutoJoinToggle && (
              <label className="flex items-start space-x-2 text-sm text-gray-700 pt-1">
                <input
                  type="checkbox"
                  checked={autoJoinWithBot}
                  onChange={(e) => setAutoJoinWithBot(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  Auto-join with recording bot{detectedPlatform ? ` (${detectedPlatform})` : ''}
                  <span className="block text-xs text-gray-500">
                    Attendee.dev joins the call as a participant and streams audio
                    to the same transcription pipeline as the browser mic.
                  </span>
                </span>
              </label>
            )}
          </div>

          {/* Meeting Type */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Meeting Type
            </label>
            <select
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="advisory">Advisory Meeting</option>
              <option value="review">Portfolio Review</option>
              <option value="consultation">Consultation</option>
              <option value="onboarding">Client Onboarding</option>
            </select>
          </div>

          {/* TASK-674 review: a daylight-saving gap or overlap. The typed wall clock is
              either not a real moment or two of them, so the RM chooses — nothing is
              submitted until they do. */}
          {dstChoice && dstChoice.kind !== 'ok' && dstChoice.kind !== 'invalid' && (
            <div
              data-testid="dst-choice"
              role="alertdialog"
              aria-label="Choose the exact time"
              className="rounded-lg border border-amber-300 bg-amber-50 p-3"
            >
              <p className="text-sm font-medium text-amber-900">
                {dstChoice.kind === 'gap'
                  ? `${scheduledTime} does not exist on this date — the clocks go forward.`
                  : `${scheduledTime} happens twice on this date — the clocks go back.`}
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Choose the time you mean. Nothing is scheduled until you do.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {dstChoice.options.map((opt) => (
                  <button
                    key={opt.iso}
                    type="button"
                    onClick={() => submitWith(opt.iso)}
                    className="rounded-lg bg-amber-700 px-3 py-2 text-sm font-medium text-white hover:bg-amber-800"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedClient || !scheduledDate || !scheduledTime || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                'Create Meeting'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};